/* ============================================
   LeadComercial — cuánto vale y para cuándo

   Vive en la barra lateral, junto a la empresa y las cotizaciones: son los
   datos de la negociación, que se consultan de reojo mientras se lee o se
   escribe en el lienzo. En el Overview ocupaban una franja entera a mitad de
   página, lejos de la cotización con la que se comparan.

   Trae su propia edición en línea, en vez de compartir la del Overview: son
   tres campos y la maquinaria de allá está atada a su grilla. Compartirla
   habría significado extraer un editor genérico para dos usos, y el segundo
   uso dejó de existir cuando este bloque se mudó.
   ============================================ */
import { useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { formatCurrency } from '@/lib/utils'
import type { Lead, MonedaLead } from '@/lib/types'
import { IconEdit, IconDollar } from '@/components/icons/Icons'

const MONEDAS: MonedaLead[] = ['PEN', 'USD']

function fechaCorta(iso: string | null): string {
  if (!iso) return '—'
  const d = iso.length <= 10 ? new Date(iso + 'T12:00:00') : new Date(iso)
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
}

interface LeadComercialProps {
  lead: Lead
  onRefresh: () => void
}

export default function LeadComercial({ lead, onRefresh }: LeadComercialProps) {
  const api = useApi()
  const [editando, setEditando] = useState<string | null>(null)
  const [borrador, setBorrador] = useState('')
  const [monedaDraft, setMonedaDraft] = useState<MonedaLead>(lead.moneda || 'PEN')
  const [error, setError] = useState<string | null>(null)

  const valorNumero = lead.valor_estimado != null ? Number(lead.valor_estimado) : null

  async function guardar(campo: string, valor: string | number | null, extra?: Record<string, unknown>) {
    setEditando(null)
    setError(null)
    try {
      await api.put(`/api/leads/${lead.id}`, { [campo]: valor, ...extra })
      onRefresh()
    } catch (err: any) {
      console.error(`Error guardando ${campo}:`, err)
      setError(err?.message || 'No se pudo guardar')
    }
  }

  /** El valor y su moneda son un solo dato: se editan juntos y se guardan juntos. */
  function guardarValor() {
    const limpio = borrador.trim()
    guardar('valor_estimado', limpio === '' ? null : Number(limpio), { moneda: monedaDraft })
  }

  function abrir(campo: string, valorActual: string) {
    setEditando(campo)
    setBorrador(valorActual)
    setError(null)
  }

  /** Campo que se edita en su propio sitio, sin modal. */
  function Campo({ campo, label, valor, tipo = 'text', mostrado, placeholder, sufijo }: {
    campo: string
    label: string
    valor: string
    tipo?: 'text' | 'number' | 'date'
    mostrado: React.ReactNode
    placeholder?: string
    sufijo?: string
  }) {
    if (editando === campo) {
      return (
        <div className="ld-dato">
          <span className="ld-dato__label">{label}</span>
          <input
            type={tipo}
            className="form-input form-input--inline-mini"
            value={borrador}
            placeholder={placeholder}
            autoFocus
            onChange={e => setBorrador(e.target.value)}
            onBlur={() => guardar(campo, borrador.trim() === '' ? null : (tipo === 'number' ? Number(borrador) : borrador.trim()))}
            onKeyDown={e => {
              if (e.key === 'Enter') e.currentTarget.blur()
              if (e.key === 'Escape') setEditando(null)
            }}
          />
        </div>
      )
    }
    return (
      <button type="button" className="ld-dato ld-dato--editable" onClick={() => abrir(campo, valor)}>
        <span className="ld-dato__label">{label}</span>
        <span className="ld-dato__valor">
          {mostrado}{sufijo}
          <IconEdit size={11} />
        </span>
      </button>
    )
  }

  return (
    <section className="ld-panel">
      <div className="ld-panel__header">
        <h4 className="ld-panel__titulo"><IconDollar size={14} /> Negociación</h4>
      </div>

      <div className="ld-datos ld-datos--filas">
        {editando === 'valor_estimado' ? (
          <div className="ld-dato">
            <span className="ld-dato__label">Valor estimado</span>
            <div className="ld-dato__valor-edit">
              <select
                className="form-input form-input--inline-mini ld-dato__select"
                value={monedaDraft}
                onChange={e => setMonedaDraft(e.target.value as MonedaLead)}
              >
                {MONEDAS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <input
                type="number"
                className="form-input form-input--inline-mini"
                value={borrador}
                placeholder="0.00"
                autoFocus
                onChange={e => setBorrador(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') guardarValor()
                  if (e.key === 'Escape') setEditando(null)
                }}
              />
              <button type="button" className="btn btn-primary btn-sm" onClick={guardarValor}>OK</button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="ld-dato ld-dato--editable"
            onClick={() => {
              setMonedaDraft(lead.moneda || 'PEN')
              abrir('valor_estimado', valorNumero != null ? String(valorNumero) : '')
            }}
          >
            <span className="ld-dato__label">Valor estimado</span>
            <span className="ld-dato__valor">
              {valorNumero != null
                ? formatCurrency(valorNumero, lead.moneda || 'PEN')
                : <span className="text-muted-italic">Sin estimar</span>}
              <IconEdit size={11} />
            </span>
          </button>
        )}

        <Campo
          campo="probabilidad"
          label="Probabilidad"
          valor={lead.probabilidad != null ? String(lead.probabilidad) : ''}
          tipo="number"
          placeholder="0–100"
          sufijo={lead.probabilidad != null ? '%' : ''}
          mostrado={lead.probabilidad != null
            ? String(lead.probabilidad)
            : <span className="text-muted-italic">Sin definir</span>}
        />

        <Campo
          campo="fecha_cierre_estimada"
          label="Cierre estimado"
          valor={lead.fecha_cierre_estimada || ''}
          tipo="date"
          mostrado={lead.fecha_cierre_estimada
            ? fechaCorta(lead.fecha_cierre_estimada)
            : <span className="text-muted-italic">Sin fecha</span>}
        />
      </div>

      {error && <p className="ld-panel__error">{error}</p>}
    </section>
  )
}
